import React, { useMemo, useState } from 'react';
import { FileDown, FileText, Ruler, LayoutGrid, Layers, GlassWater, Users, TrendingUp, Briefcase, AppWindow, DoorOpen, Scissors, PanelTop } from 'lucide-react';
import { WINDOW_TYPES } from '../../config/windowTypes';
import { useCutlistJobs } from '../../hooks/useCutlistJobs';
import { useJobOrders } from '../../hooks/useJobOrders';
import LoadingSpinner from '../ui/LoadingSpinner';
import Button from '../ui/Button';
import { DATE_PRESETS, getDateRangeForPreset } from '../../utils/report-dates';
import {
  flattenOrderedWindows,
  summarizeBySize,
  summarizeByType,
  summarizeByConfig,
  summarizeByGlass,
  summarizeByColor,
  summarizeByCustomer,
  summarizeByMonth,
} from '../../utils/window-analytics';
import {
  flattenOrderedDoors,
  summarizeDoorsByType,
  summarizeDoorsBySize,
  summarizeDoorsByMonth,
  doorSizeCoverage,
} from '../../utils/door-analytics';
import {
  flattenCutParts,
  summarizeLinearParts,
  summarizeGlassSizes,
  summarizeScreenSizes,
} from '../../utils/cutlist-parts-analytics';
import { buildWindowsAnalyticsPDF, buildDoorsAnalyticsPDF, buildCutPartsAnalyticsPDF } from '../../utils/analytics-pdf-builder';
import {
  INK, WALNUT, MOSS, OCHRE, STONE, FONT_DISPLAY, FONT_SANS,
  cardStyle, selectStyle, pct, StatCard, DataTable, MonthTrend, downloadCsv,
} from './AnalyticsTable';

const WINDOW_VIEWS = [
  { key: 'size', label: 'By Exact Size', Icon: Ruler },
  { key: 'type', label: 'By Window Type', Icon: LayoutGrid },
  { key: 'config', label: 'By Configuration', Icon: Layers },
  { key: 'glass', label: 'By Glass Package', Icon: GlassWater },
  { key: 'customer', label: 'Top Customers', Icon: Users },
  { key: 'month', label: 'Trend by Month', Icon: TrendingUp },
];

const DOOR_VIEWS = [
  { key: 'type', label: 'By Door Type', Icon: LayoutGrid },
  { key: 'size', label: 'By Exact Size', Icon: Ruler },
  { key: 'month', label: 'Trend by Month', Icon: TrendingUp },
];

const CUTPARTS_VIEWS = [
  { key: 'linear', label: 'Frame & Sash Parts', Icon: Layers },
  { key: 'glass', label: 'Glass Sizes', Icon: GlassWater },
  { key: 'screen', label: 'Screen Sizes', Icon: PanelTop },
];

const SECTION_FILTERS = [
  { key: 'all', label: 'All Sections' },
  { key: 'frame', label: 'Frame' },
  { key: 'sash', label: 'Sash' },
  { key: 'glazbead', label: 'Glazbead' },
  { key: 'reinforcement', label: 'Reinforcement' },
  { key: 'misc', label: 'Misc/Trim' },
];

export default function WindowAnalytics() {
  const { jobs, loading: windowsLoading, error: windowsError } = useCutlistJobs();
  const { jobOrders, loading: doorsLoading, error: doorsError } = useJobOrders();

  const [product, setProduct] = useState('windows'); // 'windows' | 'doors' | 'cutparts'
  const [preset, setPreset] = useState('ytd');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sectionFilter, setSectionFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [view, setView] = useState('size');
  const [exportingPdf, setExportingPdf] = useState(false);

  const loading = windowsLoading || doorsLoading;
  const error = windowsError || doorsError;

  const { from, to } = useMemo(() => getDateRangeForPreset(preset), [preset]);
  const presetLabel = DATE_PRESETS.find(p => p.key === preset)?.label || preset;

  function changeProduct(next) {
    setProduct(next);
    setView(next === 'doors' ? 'type' : next === 'cutparts' ? 'linear' : 'size');
  }

  // ── Windows ──────────────────────────────────────────────────────────────
  const windowRows = useMemo(() => {
    const all = flattenOrderedWindows(jobs, { from, to });
    return typeFilter === 'all' ? all : all.filter(r => r.type === typeFilter);
  }, [jobs, from, to, typeFilter]);

  const bySize = useMemo(() => summarizeBySize(windowRows), [windowRows]);
  const byType = useMemo(() => summarizeByType(windowRows), [windowRows]);
  const byConfig = useMemo(() => summarizeByConfig(windowRows), [windowRows]);
  const byGlass = useMemo(() => summarizeByGlass(windowRows), [windowRows]);
  const byColor = useMemo(() => summarizeByColor(windowRows), [windowRows]);
  const byCustomer = useMemo(() => summarizeByCustomer(windowRows), [windowRows]);
  const byMonth = useMemo(() => summarizeByMonth(windowRows), [windowRows]);

  const filteredBySize = useMemo(() => {
    if (!search.trim()) return bySize;
    const q = search.trim().toLowerCase();
    return bySize.filter(r =>
      r.typeLabel.toLowerCase().includes(q) ||
      r.size.toLowerCase().includes(q) ||
      r.configs.toLowerCase().includes(q) ||
      r.color.toLowerCase().includes(q)
    );
  }, [bySize, search]);

  const windowTotalQty = windowRows.reduce((sum, r) => sum + r.qty, 0);
  const windowTotalSqFt = windowRows.reduce((sum, r) => sum + r.sqFt, 0);
  const windowTotalJobs = new Set(windowRows.map(r => r.jobId)).size;
  const topType = byType[0];
  const topSize = bySize[0];
  const topColor = byColor[0];

  const windowsSummary = { totalQty: windowTotalQty, totalSqFt: windowTotalSqFt, totalJobs: windowTotalJobs, topType, topSize, topColor, bySize, byType, byConfig, byGlass, byColor, byCustomer, byMonth };

  // ── Doors ────────────────────────────────────────────────────────────────
  const doorRows = useMemo(() => flattenOrderedDoors(jobOrders, { from, to }), [jobOrders, from, to]);
  const doorsByType = useMemo(() => summarizeDoorsByType(doorRows), [doorRows]);
  const doorsBySize = useMemo(() => summarizeDoorsBySize(doorRows), [doorRows]);
  const doorsByMonth = useMemo(() => summarizeDoorsByMonth(doorRows), [doorRows]);
  const sizeCoverage = useMemo(() => doorSizeCoverage(doorRows), [doorRows]);

  const doorTotalQty = doorRows.reduce((sum, r) => sum + r.qty, 0);
  const doorTotalJobOrders = new Set(doorRows.map(r => r.jobOrderId)).size;
  const topDoorType = doorsByType[0];

  const doorsSummary = { totalQty: doorTotalQty, totalJobOrders: doorTotalJobOrders, sizeCoverage, byType: doorsByType, bySize: doorsBySize, byMonth: doorsByMonth };

  // ── Cutlist Parts (frame/sash/glazbead/reinforcement/misc + glass + screen) ─
  const { linearRows, glassRows, screenRows, coverage: cutCoverage } = useMemo(
    () => flattenCutParts(jobs, { from, to }),
    [jobs, from, to]
  );
  const filteredLinearRows = useMemo(
    () => (typeFilter === 'all' ? linearRows : linearRows.filter(r => r.type === typeFilter)),
    [linearRows, typeFilter]
  );
  const filteredGlassRows = useMemo(
    () => (typeFilter === 'all' ? glassRows : glassRows.filter(r => r.type === typeFilter)),
    [glassRows, typeFilter]
  );
  const filteredScreenRows = useMemo(
    () => (typeFilter === 'all' ? screenRows : screenRows.filter(r => r.type === typeFilter)),
    [screenRows, typeFilter]
  );

  const byLinearPartsAll = useMemo(() => summarizeLinearParts(filteredLinearRows), [filteredLinearRows]);
  const byLinearParts = useMemo(
    () => (sectionFilter === 'all' ? byLinearPartsAll : byLinearPartsAll.filter(r => r.section === sectionFilter)),
    [byLinearPartsAll, sectionFilter]
  );
  const byGlassSizes = useMemo(() => summarizeGlassSizes(filteredGlassRows), [filteredGlassRows]);
  const byScreenSizes = useMemo(() => summarizeScreenSizes(filteredScreenRows), [filteredScreenRows]);

  const topFrameHeader = byLinearPartsAll.find(r => r.partLabel === 'Frame Header');
  const topKSashHeight = byLinearPartsAll.find(r => r.partLabel === 'K Sash Heights');
  const topGlassSize = byGlassSizes[0];
  const topScreenSize = byScreenSizes[0];

  const cutPartsSummary = { coverage: cutCoverage, byLinearParts, byGlassSizes, byScreenSizes };

  const cutPartsRowCount = filteredLinearRows.length + filteredGlassRows.length + filteredScreenRows.length;
  const rows = product === 'windows' ? windowRows : product === 'doors' ? doorRows : filteredLinearRows;
  const isEmpty = product === 'cutparts' ? cutPartsRowCount === 0 : rows.length === 0;

  function handleExportCsv() {
    if (product === 'windows') {
      const exporters = {
        size: () => [['Type', 'Size', 'Color', 'Configs', 'Times Ordered', 'Total Qty', 'Jobs', '% of Total'],
          filteredBySize.map(r => [r.typeLabel, r.size, r.color, r.configs, r.timesOrdered, r.totalQty, r.jobCount, pct(r)])],
        type: () => [['Type', 'Times Ordered', 'Total Qty', 'Jobs', '% of Total'],
          byType.map(r => [r.typeLabel, r.timesOrdered, r.totalQty, r.jobCount, pct(r)])],
        config: () => [['Type', 'Configuration', 'Times Ordered', 'Total Qty', 'Jobs', '% of Total'],
          byConfig.map(r => [r.typeLabel, r.config, r.timesOrdered, r.totalQty, r.jobCount, pct(r)])],
        glass: () => [['Glass Type', 'Glass Config', 'Times Ordered', 'Total Qty', 'Jobs', '% of Total'],
          byGlass.map(r => [r.glassType, r.glassConfig, r.timesOrdered, r.totalQty, r.jobCount, pct(r)])],
        customer: () => [['Customer', 'Jobs', 'Windows Ordered', '% of Total'],
          byCustomer.map(r => [r.customerName, r.jobCount, r.totalQty, pct(r)])],
        month: () => [['Month', 'Times Ordered', 'Total Qty'],
          byMonth.map(r => [r.label, r.timesOrdered, r.totalQty])],
      };
      const [headers, csvRows] = exporters[view]();
      downloadCsv(headers, csvRows, `windows-${view}-${preset}-${new Date().toISOString().slice(0, 10)}.csv`);
    } else if (product === 'doors') {
      const exporters = {
        type: () => [['Door Type', 'Color', 'Times Ordered', 'Total Qty', 'Job Orders', '% of Total'],
          doorsByType.map(r => [r.style, r.color, r.timesOrdered, r.totalQty, r.jobCount, pct(r)])],
        size: () => [['Door Type', 'Size', 'Color', 'Times Ordered', 'Total Qty', 'Job Orders', '% of Total'],
          doorsBySize.map(r => [r.style, r.size, r.color, r.timesOrdered, r.totalQty, r.jobCount, pct(r)])],
        month: () => [['Month', 'Times Ordered', 'Total Qty'],
          doorsByMonth.map(r => [r.label, r.timesOrdered, r.totalQty])],
      };
      const [headers, csvRows] = exporters[view]();
      downloadCsv(headers, csvRows, `doors-${view}-${preset}-${new Date().toISOString().slice(0, 10)}.csv`);
    } else {
      const exporters = {
        linear: () => [['Type', 'Section', 'Part', 'Length', 'Color', 'Part #', 'Times Ordered', 'Pieces Needed', 'Jobs', '% of Total'],
          byLinearParts.map(r => [r.typeLabel, r.sectionLabel, r.partLabel, `${r.length}"`, r.color, r.partNumbers, r.timesOrdered, r.totalQty, r.jobCount, pct(r)])],
        glass: () => [['Type', 'Pane', 'Size', 'Color', 'Times Ordered', 'Total Qty', 'Jobs', '% of Total'],
          byGlassSizes.map(r => [r.typeLabel, r.paneLabel, r.size, r.color, r.timesOrdered, r.totalQty, r.jobCount, pct(r)])],
        screen: () => [['Type', 'Screen', 'Size', 'Color', 'Times Ordered', 'Total Qty', 'Jobs', '% of Total'],
          byScreenSizes.map(r => [r.typeLabel, r.screenLabel, r.size, r.color, r.timesOrdered, r.totalQty, r.jobCount, pct(r)])],
      };
      const [headers, csvRows] = exporters[view]();
      downloadCsv(headers, csvRows, `cut-parts-${view}-${preset}-${new Date().toISOString().slice(0, 10)}.csv`);
    }
  }

  async function handleExportPdf() {
    setExportingPdf(true);
    try {
      const typeFilterLabel = typeFilter !== 'all' ? WINDOW_TYPES.find(t => t.code === typeFilter)?.label : null;
      const dateStamp = new Date().toISOString().slice(0, 10);
      let doc, filename;

      if (product === 'windows') {
        doc = await buildWindowsAnalyticsPDF({ presetLabel, typeFilterLabel, windows: windowsSummary });
        filename = `windows-analytics-${preset}-${dateStamp}.pdf`;
      } else if (product === 'doors') {
        doc = await buildDoorsAnalyticsPDF({ presetLabel, doors: doorsSummary });
        filename = `doors-analytics-${preset}-${dateStamp}.pdf`;
      } else {
        const sectionFilterLabel = sectionFilter !== 'all' ? SECTION_FILTERS.find(s => s.key === sectionFilter)?.label : null;
        doc = await buildCutPartsAnalyticsPDF({ presetLabel, typeFilterLabel, sectionFilterLabel, cutParts: cutPartsSummary });
        filename = `cut-parts-analytics-${preset}-${dateStamp}.pdf`;
      }

      doc.save(filename);
    } catch (err) {
      console.error('Error building analytics PDF:', err);
      alert('Failed to generate the PDF. Please try again.');
    } finally {
      setExportingPdf(false);
    }
  }

  if (loading) {
    return (
      <div style={{ ...cardStyle, display: 'flex', justifyContent: 'center', padding: '60px' }}>
        <LoadingSpinner size="large" color={WALNUT} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={cardStyle}>
        <p style={{ color: '#dc2626', fontSize: '13px', margin: 0 }}>Failed to load analytics data. Please try again.</p>
      </div>
    );
  }

  const views = product === 'windows' ? WINDOW_VIEWS : product === 'doors' ? DOOR_VIEWS : CUTPARTS_VIEWS;

  return (
    <div style={{ fontFamily: FONT_SANS }}>
      {/* Header */}
      <div style={cardStyle}>
        <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: '24px', fontWeight: '600', color: INK, margin: '0 0 4px 0' }}>
          Sales Analytics
        </h2>
        <p style={{ fontSize: '13px', color: STONE, margin: 0 }}>
          What we actually sell — exact types and sizes, ranked by how often they are ordered. Sizes and colors are matched exactly — nothing gets rounded or blended together. Only sold-or-further jobs count.
        </p>
      </div>

      {/* Product toggle */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {[{ key: 'windows', label: 'Windows', Icon: AppWindow }, { key: 'doors', label: 'Doors', Icon: DoorOpen }, { key: 'cutparts', label: 'Cut Parts', Icon: Scissors }].map(p => {
          const active = product === p.key;
          return (
            <button
              key={p.key}
              onClick={() => changeProduct(p.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '10px 18px', borderRadius: '10px', border: `2px solid ${active ? WALNUT : 'rgba(139,69,19,0.15)'}`,
                background: active ? WALNUT : 'white', color: active ? 'white' : INK,
                fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: FONT_SANS,
              }}
            >
              <p.Icon size={16} strokeWidth={2} /> {p.label}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div style={{ ...cardStyle, display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: INK, marginBottom: '6px' }}>Date Range</label>
          <select value={preset} onChange={e => setPreset(e.target.value)} style={selectStyle}>
            {DATE_PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>
        {(product === 'windows' || product === 'cutparts') && (
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: INK, marginBottom: '6px' }}>Window Type</label>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={selectStyle}>
              <option value="all">All Types</option>
              {WINDOW_TYPES.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
          </div>
        )}
        {product === 'cutparts' && view === 'linear' && (
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: INK, marginBottom: '6px' }}>Section</label>
            <select value={sectionFilter} onChange={e => setSectionFilter(e.target.value)} style={selectStyle}>
              {SECTION_FILTERS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        )}
        {product === 'windows' && (
          <div style={{ flex: '1 1 200px', minWidth: '160px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: INK, marginBottom: '6px' }}>Search (Size view only)</label>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Filter by size, config, color..."
              style={{ ...selectStyle, width: '100%', boxSizing: 'border-box' }}
            />
          </div>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          <Button variant="secondary" size="small" onClick={handleExportCsv}>
            <FileDown size={14} strokeWidth={2} /> Export CSV
          </Button>
          <Button variant="primary" size="small" onClick={handleExportPdf} disabled={exportingPdf}>
            <FileText size={14} strokeWidth={2} /> {exportingPdf ? 'Building PDF...' : 'Export PDF'}
          </Button>
        </div>
      </div>

      {isEmpty ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: '48px 20px', color: STONE }}>
          {product === 'windows'
            ? 'No sold windows found for this range. Try widening the date range or clearing the type filter.'
            : product === 'doors'
            ? 'No ordered doors found for this range. Try widening the date range.'
            : 'No cut-list data found for this range. Cut sizes are only available once a job\'s cut list has been generated.'}
        </div>
      ) : product === 'windows' ? (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
            <StatCard label="Windows Ordered" value={windowTotalQty.toLocaleString()} sub={`${windowTotalJobs} job${windowTotalJobs === 1 ? '' : 's'}`} color={WALNUT} />
            <StatCard label="Most Ordered Type" value={topType?.typeLabel || '—'} sub={topType ? `${topType.totalQty} units` : ''} color={OCHRE} />
            <StatCard label="Most Ordered Size" value={topSize?.size || '—'} sub={topSize ? `${topSize.typeLabel} · ${topSize.color} · ordered ${topSize.timesOrdered}×` : ''} color={MOSS} />
            <StatCard label="Total Sq Ft Ordered" value={Math.round(windowTotalSqFt).toLocaleString()} sub="glass + frame area" color={WALNUT} />
            <StatCard label="Most Ordered Color" value={topColor?.color || '—'} sub={topColor ? `${topColor.totalQty} units` : ''} color={OCHRE} />
            <StatCard label="Distinct Sizes" value={bySize.length.toLocaleString()} sub="unique type + size combos" color={STONE} />
          </div>

          <ViewTabs views={views} active={view} onChange={setView} />

          <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
            {view === 'size' && (
              <DataTable
                key="w-size"
                defaultSort="totalQty"
                minWidth="760px"
                rows={filteredBySize}
                columns={[
                  { field: 'typeLabel', label: 'Type', bold: true },
                  { field: 'size', label: 'Size' },
                  { field: 'color', label: 'Color', bold: true },
                  { field: 'configs', label: 'Configs', muted: true },
                  { field: 'timesOrdered', label: 'Times Ordered', align: 'right' },
                  { field: 'totalQty', label: 'Total Qty', align: 'right', emphasize: true },
                  { field: 'jobCount', label: 'Jobs', align: 'right', muted: true },
                  { field: 'pctOfTotal', label: '% of Total', align: 'right', muted: true, render: pct },
                ]}
              />
            )}
            {view === 'type' && (
              <DataTable key="w-type" defaultSort="totalQty" rows={byType} columns={[
                { field: 'typeLabel', label: 'Type', bold: true },
                { field: 'timesOrdered', label: 'Times Ordered', align: 'right' },
                { field: 'totalQty', label: 'Total Qty', align: 'right', emphasize: true },
                { field: 'jobCount', label: 'Jobs', align: 'right', muted: true },
                { field: 'pctOfTotal', label: '% of Total', align: 'right', muted: true, render: pct },
              ]} />
            )}
            {view === 'config' && (
              <DataTable key="w-config" defaultSort="totalQty" rows={byConfig} columns={[
                { field: 'typeLabel', label: 'Type', bold: true },
                { field: 'config', label: 'Configuration' },
                { field: 'timesOrdered', label: 'Times Ordered', align: 'right' },
                { field: 'totalQty', label: 'Total Qty', align: 'right', emphasize: true },
                { field: 'jobCount', label: 'Jobs', align: 'right', muted: true },
                { field: 'pctOfTotal', label: '% of Total', align: 'right', muted: true, render: pct },
              ]} />
            )}
            {view === 'glass' && (
              <DataTable key="w-glass" defaultSort="totalQty" rows={byGlass} columns={[
                { field: 'glassType', label: 'Glass Type', bold: true },
                { field: 'glassConfig', label: 'Glass Config' },
                { field: 'timesOrdered', label: 'Times Ordered', align: 'right' },
                { field: 'totalQty', label: 'Total Qty', align: 'right', emphasize: true },
                { field: 'jobCount', label: 'Jobs', align: 'right', muted: true },
                { field: 'pctOfTotal', label: '% of Total', align: 'right', muted: true, render: pct },
              ]} />
            )}
            {view === 'customer' && (
              <DataTable key="w-customer" defaultSort="totalQty" rows={byCustomer} columns={[
                { field: 'customerName', label: 'Customer', bold: true },
                { field: 'jobCount', label: 'Jobs', align: 'right' },
                { field: 'totalQty', label: 'Windows Ordered', align: 'right', emphasize: true },
                { field: 'pctOfTotal', label: '% of Total', align: 'right', muted: true, render: pct },
              ]} />
            )}
            {view === 'month' && <MonthTrend byMonth={byMonth} />}
          </div>

          <p style={{ fontSize: '11px', color: STONE, marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Briefcase size={12} /> Based on {windowTotalJobs} sold-or-further job{windowTotalJobs === 1 ? '' : 's'} and {windowRows.length} window line item{windowRows.length === 1 ? '' : 's'}.
          </p>
        </>
      ) : product === 'doors' ? (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
            <StatCard label="Doors Ordered" value={doorTotalQty.toLocaleString()} sub={`${doorTotalJobOrders} job order${doorTotalJobOrders === 1 ? '' : 's'}`} color={WALNUT} />
            <StatCard label="Most Ordered Type" value={topDoorType?.style || '—'} sub={topDoorType ? `${topDoorType.color} · ${topDoorType.totalQty} units` : ''} color={OCHRE} />
            <StatCard label="With Recorded Size" value={sizeCoverage.sized} sub={`of ${sizeCoverage.total} door line items`} color={MOSS} />
            <StatCard label="Without Recorded Size" value={sizeCoverage.unsized} sub="not measured on the order" color={STONE} />
          </div>

          <p style={{ fontSize: '12px', color: STONE, marginBottom: '12px' }}>
            Doors are estimate line items, not manufactured cutlist items — width/height is only tracked when someone actually entered it on the order, so size reporting only covers doors that have one.
          </p>

          <ViewTabs views={views} active={view} onChange={setView} />

          <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
            {view === 'type' && (
              <DataTable key="d-type" defaultSort="totalQty" rows={doorsByType} columns={[
                { field: 'style', label: 'Door Type', bold: true },
                { field: 'color', label: 'Color', bold: true },
                { field: 'timesOrdered', label: 'Times Ordered', align: 'right' },
                { field: 'totalQty', label: 'Total Qty', align: 'right', emphasize: true },
                { field: 'jobCount', label: 'Job Orders', align: 'right', muted: true },
                { field: 'pctOfTotal', label: '% of Total', align: 'right', muted: true, render: pct },
              ]} />
            )}
            {view === 'size' && (
              <DataTable
                key="d-size"
                defaultSort="totalQty"
                rows={doorsBySize}
                emptyLabel="No doors in this range have a recorded width/height."
                columns={[
                  { field: 'style', label: 'Door Type', bold: true },
                  { field: 'size', label: 'Size' },
                  { field: 'color', label: 'Color', bold: true },
                  { field: 'timesOrdered', label: 'Times Ordered', align: 'right' },
                  { field: 'totalQty', label: 'Total Qty', align: 'right', emphasize: true },
                  { field: 'jobCount', label: 'Job Orders', align: 'right', muted: true },
                  { field: 'pctOfTotal', label: '% of Total', align: 'right', muted: true, render: pct },
                ]}
              />
            )}
            {view === 'month' && <MonthTrend byMonth={doorsByMonth} />}
          </div>

          <p style={{ fontSize: '11px', color: STONE, marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Briefcase size={12} /> Based on {doorTotalJobOrders} approved-or-further job order{doorTotalJobOrders === 1 ? '' : 's'} and {doorRows.length} door line item{doorRows.length === 1 ? '' : 's'}.
          </p>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
            <StatCard label="Jobs With Cut Data" value={`${cutCoverage.jobsWithCalc} / ${cutCoverage.totalJobs}`} sub="have a generated cut list" color={WALNUT} />
            <StatCard label="Top Frame Header (DH)" value={topFrameHeader ? `${topFrameHeader.length}"` : '—'} sub={topFrameHeader ? `${topFrameHeader.color} · ${topFrameHeader.totalQty} pieces needed` : ''} color={OCHRE} />
            <StatCard label="Top K Sash Height (DH)" value={topKSashHeight ? `${topKSashHeight.length}"` : '—'} sub={topKSashHeight ? `${topKSashHeight.color} · ${topKSashHeight.totalQty} pieces needed` : ''} color={MOSS} />
            <StatCard label="Top Glass Size" value={topGlassSize ? `${topGlassSize.typeLabel} ${topGlassSize.paneLabel}` : '—'} sub={topGlassSize ? `${topGlassSize.color} · ${topGlassSize.size} · ${topGlassSize.totalQty} units` : ''} color={WALNUT} />
            <StatCard label="Top Screen Size" value={topScreenSize ? `${topScreenSize.typeLabel} ${topScreenSize.screenLabel}` : '—'} sub={topScreenSize ? `${topScreenSize.color} · ${topScreenSize.size} · ${topScreenSize.totalQty} units` : ''} color={OCHRE} />
          </div>

          <p style={{ fontSize: '12px', color: STONE, marginBottom: '12px' }}>
            Pulled from the actual cut list (frame, sash, glazbead, reinforcement, glass, and screen dimensions) rather than the overall window size — this is what you would pre-cut and shelve. Only windows whose job has actually had its cut list generated are counted, and sizes match exactly (down to the fraction).
          </p>

          <ViewTabs views={views} active={view} onChange={setView} />

          <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
            {view === 'linear' && (
              <DataTable
                key="c-linear"
                defaultSort="totalQty"
                minWidth="820px"
                rows={byLinearParts}
                emptyLabel="No parts match this section filter."
                columns={[
                  { field: 'typeLabel', label: 'Type', bold: true },
                  { field: 'sectionLabel', label: 'Section', muted: true },
                  { field: 'partLabel', label: 'Part' },
                  { field: 'length', label: 'Length (in)', align: 'right', render: r => `${r.length}"` },
                  { field: 'color', label: 'Color', bold: true },
                  { field: 'partNumbers', label: 'Part #', muted: true },
                  { field: 'timesOrdered', label: 'Times Ordered', align: 'right' },
                  { field: 'totalQty', label: 'Pieces Needed', align: 'right', emphasize: true },
                  { field: 'jobCount', label: 'Jobs', align: 'right', muted: true },
                  { field: 'pctOfTotal', label: '% of Total', align: 'right', muted: true, render: pct },
                ]}
              />
            )}
            {view === 'glass' && (
              <DataTable
                key="c-glass"
                defaultSort="totalQty"
                rows={byGlassSizes}
                emptyLabel="No glass sizes found — cut lists may not be generated for this range yet."
                columns={[
                  { field: 'typeLabel', label: 'Type', bold: true },
                  { field: 'paneLabel', label: 'Pane' },
                  { field: 'size', label: 'Size' },
                  { field: 'color', label: 'Color', bold: true },
                  { field: 'timesOrdered', label: 'Times Ordered', align: 'right' },
                  { field: 'totalQty', label: 'Total Qty', align: 'right', emphasize: true },
                  { field: 'jobCount', label: 'Jobs', align: 'right', muted: true },
                  { field: 'pctOfTotal', label: '% of Total', align: 'right', muted: true, render: pct },
                ]}
              />
            )}
            {view === 'screen' && (
              <DataTable
                key="c-screen"
                defaultSort="totalQty"
                rows={byScreenSizes}
                emptyLabel="No screen sizes found for this range."
                columns={[
                  { field: 'typeLabel', label: 'Type', bold: true },
                  { field: 'screenLabel', label: 'Screen' },
                  { field: 'size', label: 'Size' },
                  { field: 'color', label: 'Color', bold: true },
                  { field: 'timesOrdered', label: 'Times Ordered', align: 'right' },
                  { field: 'totalQty', label: 'Total Qty', align: 'right', emphasize: true },
                  { field: 'jobCount', label: 'Jobs', align: 'right', muted: true },
                  { field: 'pctOfTotal', label: '% of Total', align: 'right', muted: true, render: pct },
                ]}
              />
            )}
          </div>

          <p style={{ fontSize: '11px', color: STONE, marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Briefcase size={12} /> Based on {cutCoverage.jobsWithCalc} job{cutCoverage.jobsWithCalc === 1 ? '' : 's'} with a generated cut list ({cutCoverage.jobsWithoutCalc} sold job{cutCoverage.jobsWithoutCalc === 1 ? '' : 's'} in range not yet counted here).
          </p>
        </>
      )}
    </div>
  );
}

function ViewTabs({ views, active, onChange }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
      {views.map(v => {
        const isActive = active === v.key;
        return (
          <button
            key={v.key}
            onClick={() => onChange(v.key)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '8px 14px', borderRadius: '8px', border: `1px solid ${isActive ? WALNUT : 'rgba(139,69,19,0.15)'}`,
              background: isActive ? `${WALNUT}12` : 'white', color: isActive ? WALNUT : STONE,
              fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: FONT_SANS,
            }}
          >
            <v.Icon size={14} strokeWidth={2} /> {v.label}
          </button>
        );
      })}
    </div>
  );
}
